import { ChevronUp, ChevronDown, MessageCircle, Lock, Eye } from 'lucide-react'
import type { ReactNode } from 'react'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/presentation/components/ui/table'
import { Badge } from '@/presentation/components/ui/badge'
import { Button } from '@/presentation/components/ui/button'
import { DriverAvatar } from '@/presentation/components/ui/driver-avatar'
import { Driver } from '@/core/domain/entities/Driver'
import { formatCurrency } from '@/lib/utils'
import DriverStatsColumnSkeleton from '../Skeletons/DriverStatsColumnSkeleton'
import { useNavigateWithParams } from '@/presentation/hooks/useNavigateWithParams'

type DriverStatistics = {
  driverId: string
  totalRides: number
  completedRides: number
  comissionEarnings: number
}

type DriversTableProps = {
  filteredDrivers: Driver[]
  driverStatisticsMap: Map<string, DriverStatistics>
  sortByField: string
  sortOrder: 'asc' | 'desc'
  handleSort: (field: string) => void
  isLoadingStats?: boolean
}

const SortableTableHead = ({
  field,
  children,
  className = '',
  sortByField,
  sortOrder,
  handleSort,
}: {
  field: string
  children: ReactNode
  className?: string
  sortByField: string
  sortOrder: 'asc' | 'desc'
  handleSort: (field: string) => void
}) => {
  const isFieldActive = sortByField === field
  const shouldShowArrow = isFieldActive

  return (
    <TableHead
      className={`cursor-pointer select-none hover:bg-muted/50 transition-colors touch-manipulation min-h-[44px] ${className}`}
      onClick={() => handleSort(field)}
    >
      <div className="flex items-center gap-1 justify-between sm:justify-start">
        <span className="text-sm font-medium">{children}</span>
        {shouldShowArrow &&
          (sortOrder === 'asc' ? (
            <ChevronUp className="h-4 w-4 flex-shrink-0" />
          ) : (
            <ChevronDown className="h-4 w-4 flex-shrink-0" />
          ))}
      </div>
    </TableHead>
  )
}

export default function DriversTable({
  filteredDrivers,
  driverStatisticsMap,
  sortByField,
  sortOrder,
  handleSort,
  isLoadingStats = false,
}: DriversTableProps) {
  const { navigateWithParams } = useNavigateWithParams()

  const handleDriverClick = (driverId: string) => {
    navigateWithParams(`/driver/${driverId}`)
  }

  return (
    <Table>
      <TableHeader>
        <TableRow>
          <SortableTableHead
            field="name"
            sortByField={sortByField}
            sortOrder={sortOrder}
            handleSort={handleSort}
          >
            Motorista
          </SortableTableHead>
          <SortableTableHead
            field="status"
            sortByField={sortByField}
            sortOrder={sortOrder}
            handleSort={handleSort}
          >
            Status
          </SortableTableHead>
          <SortableTableHead
            field="totalRides"
            sortByField={sortByField}
            sortOrder={sortOrder}
            handleSort={handleSort}
          >
            Corridas
          </SortableTableHead>
          <SortableTableHead
            field="commission"
            sortByField={sortByField}
            sortOrder={sortOrder}
            handleSort={handleSort}
          >
            Comissão Gerada
          </SortableTableHead>
          <SortableTableHead
            field="subscription"
            sortByField={sortByField}
            sortOrder={sortOrder}
            handleSort={handleSort}
          >
            Assinatura
          </SortableTableHead>
          <SortableTableHead
            field="classification"
            sortByField={sortByField}
            sortOrder={sortOrder}
            handleSort={handleSort}
          >
            Classificação
          </SortableTableHead>
          <SortableTableHead
            field="rating"
            sortByField={sortByField}
            sortOrder={sortOrder}
            handleSort={handleSort}
          >
            Avaliação
          </SortableTableHead>
          <SortableTableHead
            field="lastRide"
            sortByField={sortByField}
            sortOrder={sortOrder}
            handleSort={handleSort}
          >
            Última Corrida
          </SortableTableHead>
          <TableHead className="text-center">WhatsApp</TableHead>
          <TableHead className="text-center">Ações</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {filteredDrivers.length === 0 ? (
          <TableRow>
            <TableCell colSpan={9} className="text-center h-24">
              <div className="text-muted-foreground text-sm sm:text-base">
                Nenhum motorista encontrado
              </div>
            </TableCell>
          </TableRow>
        ) : (
          filteredDrivers.map((currentDriver) => {
            const driverStatistics = driverStatisticsMap.get(currentDriver.id)
            const isRestricted = currentDriver.acceptsCash === 2
            return (
              <TableRow
                key={currentDriver.id}
                className={isRestricted ? 'bg-pinkGarupa/10' : ''}
              >
                <TableCell className="min-h-[44px]">
                  <div className="flex flex-col items-center gap-2 sm:flex-row sm:items-center sm:gap-3">
                    <div className="relative">
                      <DriverAvatar
                        photo={currentDriver.photo}
                        name={currentDriver.fullName}
                        size="sm"
                      />
                      {isRestricted && (
                        <div className="absolute -top-2 -right-2 bg-pinkGarupa rounded-full p-1">
                          <Lock className="h-3 w-3 text-white" />
                        </div>
                      )}
                    </div>
                    <div className="flex items-center  gap-2">
                      <span className="font-medium text-xs text-center sm:text-left sm:text-base">
                        {currentDriver.firstName ?? 'Sem nome'}
                      </span>
                      {isRestricted && (
                        <span className="text-pinkGarupa font-semibold text-xs">
                          Restrito
                        </span>
                      )}
                    </div>
                  </div>
                </TableCell>
                <TableCell className="min-h-[44px]">
                  <Badge
                    variant={currentDriver.isOnline ? 'default' : 'secondary'}
                    className="text-xs"
                  >
                    {currentDriver.isOnline ? 'Online' : 'Offline'}
                  </Badge>
                </TableCell>
                <TableCell className="font-medium min-h-[44px] text-sm sm:text-base">
                  {isLoadingStats ? (
                    <DriverStatsColumnSkeleton />
                  ) : (
                    (driverStatistics?.totalRides ?? 0)
                  )}
                </TableCell>
                <TableCell className="font-medium text-green-600 min-h-[44px] text-sm sm:text-base">
                  {isLoadingStats ? (
                    <DriverStatsColumnSkeleton />
                  ) : (
                    formatCurrency(driverStatistics?.comissionEarnings ?? 0)
                  )}
                </TableCell>
                <TableCell className="min-h-[44px]">
                  <Badge
                    variant={
                      currentDriver.hasSubscription ? 'default' : 'secondary'
                    }
                    className="text-xs"
                  >
                    {currentDriver.hasSubscription ? 'Ativa' : 'Inativa'}
                  </Badge>
                </TableCell>
                <TableCell>
                  <div className="flex items-center gap-1">
                    <span className="text-sm sm:text-base">
                      {currentDriver.classification}
                    </span>
                  </div>
                </TableCell>
                <TableCell className="min-h-[44px]">
                  <div className="flex items-center gap-1">
                    <span className="text-sm sm:text-base">
                      {(currentDriver.rating ?? 0).toFixed(1)}
                    </span>
                    <span className="text-yellow-500 text-sm">⭐</span>
                  </div>
                </TableCell>
                <TableCell className="text-muted-foreground min-h-[44px] text-sm sm:text-base">
                  {currentDriver.lastRideAt ?? '-'}
                </TableCell>
                <TableCell className="text-center min-h-[44px]">
                  {currentDriver.phone ? (
                    <Button
                      asChild
                      variant="default"
                      size="sm"
                      className="bg-green-600 hover:bg-green-700 text-white"
                    >
                      <a
                        href={`https://wa.me/55${currentDriver.phone}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        title="Enviar mensagem no WhatsApp"
                      >
                        <MessageCircle className="h-4 w-4" />
                      </a>
                    </Button>
                  ) : (
                    <Button
                      disabled
                      variant="secondary"
                      size="sm"
                      title="Número não disponível"
                    >
                      <MessageCircle className="h-4 w-4" />
                    </Button>
                  )}
                </TableCell>
                <TableCell>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleDriverClick(currentDriver.id)}
                  >
                    <Eye className="h-4 w-4" />
                  </Button>
                </TableCell>
              </TableRow>
            )
          })
        )}
      </TableBody>
    </Table>
  )
}
